import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  hideAdGroupDurationEdit,
  hidePreview,
  setEndDate,
  setEndDateDisabled,
  setStartDate,
  setStartDateDisabled,
  showPreview,
  updateAdGroupsDuration,
} from 'features/dashboard/operation/adGroupDurationEdit/adGroupDurationEditSlice'
import { Modal } from 'components/dialog/Modals'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import { isEmptyObject } from 'utils'
import Table from 'components/table/Table'
import CheckBox from 'components/form/CheckBox'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import DatePicker from 'components/calendar/DatePicker'
import { maximum } from 'components/calendar/calendarUtil'
import moment from 'moment'
import FixedTable from 'components/table/FixedTable'

const AdGroupDurationEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: { startDate, endDate, startDateDisabled, endDateDisabled },
    preview: {
      startDate: previewStartDate,
      endDate: previewEndDate,
      startDateDisabled: previewStartDateDisabled,
      endDateDisabled: previewEndDateDisabled,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupDurationEdit,
    shallowEqual
  )
  const { errors, validate } = useValidator(
    () => ({
      duration:
        (!endDateDisabled &&
          endDate === null &&
          '종료일을 설정해주세요. 종료일없이 설정하실 경우 종료일 미설정을 선택하세요.') ||
        (!startDateDisabled &&
          !endDateDisabled &&
          startDate > endDate &&
          '종료일은 시작일보다 앞설 수 없습니다. 시작일 이후의 날짜를 선택해주세요.'),
    }),
    [startDate, startDateDisabled, endDate, endDateDisabled]
  )
  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview())
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(updateAdGroupsDuration())
        if (error) {
          preventClose()
          fail(
            '광고그룹 집행일자 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 집행일자 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideAdGroupDurationEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupDurationEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    startDate !== previewStartDate ||
    endDate !== previewEndDate ||
    startDateDisabled !== previewStartDateDisabled ||
    endDateDisabled !== previewEndDateDisabled

  return (
    <Modal on={visible} className="date_layer">
      <PreviewConfirmModal
        title="광고그룹 집행일자 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideAdGroupDurationEdit())}>
        <p className="desc_copy">
          광고가 노출되는 기간을 설정합니다. 종료일 미설정시 종료기간 설정 없이
          광고를 계속 노출합니다.
        </p>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="group_date">
              <strong className="tit_date">시작일</strong>
              <div className="group_calendar">
                <DatePicker
                  value={startDate}
                  onChange={v => dispatch(setStartDate(v))}
                  min={moment().format(moment.HTML5_FMT.DATE)}
                  max={endDate}
                  disabled={startDateDisabled}
                />
              </div>
              <CheckBox
                label="시작일 미변경"
                checked={startDateDisabled}
                onCheckedChange={v => dispatch(setStartDateDisabled(v))}
              />
            </div>
            <div className="group_date">
              <strong className="tit_date">종료일</strong>
              <div className="group_calendar">
                <DatePicker
                  value={endDate}
                  onChange={v => dispatch(setEndDate(v))}
                  min={maximum(
                    startDate,
                    moment().format(moment.HTML5_FMT.DATE)
                  )}
                  disabled={endDateDisabled}
                  error={errors.duration}
                />
              </div>
              <CheckBox
                label="종료일 미설정"
                checked={endDateDisabled}
                onCheckedChange={v => dispatch(setEndDateDisabled(v))}
              />
            </div>
            {errors.duration && <p className="txt_error">{errors.duration}</p>}
            {preview && (
              <FixedTable caption="광고그룹 집행기간 변경 이력 표">
                <Table.Head>
                  <Table.Column
                    title="선택 광고그룹"
                    className="inner_cell"
                    width={193}
                  />
                  <Table.Column
                    title="변경 전"
                    className="inner_cell"
                    width={174}
                  />
                  <Table.Column title="변경 후" className="inner_cell" />
                </Table.Head>
                <Table.Body>
                  {adGroups.map(({ id, name, impressionSchedule }) => (
                    <Table.Row
                      rowId={id}
                      key={id}
                      className={classNames({
                        update:
                          (!previewStartDateDisabled &&
                            previewStartDate !==
                              impressionSchedule.startDate) ||
                          impressionSchedule.endDate !==
                            (previewEndDateDisabled ? null : previewEndDate),
                      })}>
                      <td>
                        <div className="inner_cell">
                          <span className="txt_limit2">{name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="inner_cell">
                          {impressionSchedule.startDate} ~<br />
                          {impressionSchedule.endDate || '종료일 미설정'}
                        </div>
                      </td>
                      <td>
                        <div className="inner_cell">
                          {previewStartDateDisabled
                            ? impressionSchedule.startDate
                            : previewStartDate}{' '}
                          ~<br />
                          {previewEndDateDisabled
                            ? '종료일 미설정'
                            : previewEndDate}
                        </div>
                      </td>
                    </Table.Row>
                  ))}
                </Table.Body>
              </FixedTable>
            )}
          </>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupDurationEditModal
